import { convertAsciiMathToLatex, convertLatexToMathMl } from "mathlive";
import { convertLatexToMarkup } from "mathlive";

function generateRandomId(prefix = "") {
  const now = new Date();

  const seconds =
    now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

  const random = Math.random().toString(36).substr(2, 3);

  return prefix + seconds.toString().slice(-3) + random;
}

const handleDisplaytoInline = (editor, node) => {
  const allInlineIdSorted = editor.dom
    .select("inline-formula")
    .map((el) => el.getAttribute("id"))
    .filter((id) => id)
    .sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ""), 10);
      const numB = parseInt(b.replace(/\D/g, ""), 10);
      return numA - numB;
    });

  const getid = allInlineIdSorted[allInlineIdSorted.length - 1];
  const newIdNum = parseInt(getid.replace(/\D/g, ""), 10) + 1;
  const newId = `inl${newIdNum}`;
  const newKey = generateRandomId("in_");
  const parentElement = node?.parentNode;
  const prevP = parentElement?.previousElementSibling;
  const formulaContent = node.innerHTML;

  const inlineFormula = editor.dom.create(
    "inline-formula",
    {
      id: newId,
      key: newKey,
    },
    formulaContent
  );
  if (prevP && prevP.tagName === "P") {
    prevP.appendChild(inlineFormula);
  } else if (prevP && prevP.tagName.toLowerCase() === "list-item") {
    let p1 = prevP.querySelector("p");
    p1.appendChild(inlineFormula);
  } else {
    editor.dom.replace(inlineFormula, parentElement);
  }
  editor.dom.remove(parentElement);
  ReorderEquations(editor);
};

const handleInlinetoDisplay = (editor, node) => {
  const allDisplayIdSorted = editor.dom
    .select("disp-formula")
    .map((el) => el.getAttribute("id"))
    .filter((id) => id)
    .sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ""), 10);
      const numB = parseInt(b.replace(/\D/g, ""), 10);
      return numA - numB;
    });

  const lastId =
    allDisplayIdSorted.length > 0
      ? allDisplayIdSorted[allDisplayIdSorted.length - 1]
      : null;
  const lastNum = lastId ? parseInt(lastId.replace(/\D/g, ""), 10) + 1 : 1;
  const newId = `teqn-${lastNum}`;
  const newKey = generateRandomId("dis_");
  const pKey = generateRandomId("dfo_");
  const lKey = generateRandomId("dfl_");

  const p1 = editor.dom.create("p", { key: pKey });

  const label = editor.dom.create("label", { key: lKey }, " () ");

  const formulaContent = node.innerHTML;
  const displayFormula = editor.dom.create(
    "disp-formula",
    {
      id: newId,
      key: newKey,
    },
    formulaContent
  );

  editor.dom.add(p1, displayFormula);
  editor.dom.add(p1, label);
  editor.dom.replace(p1, node);
};

// -----------------------------MARK:Text to Math---------------------------------

function convertTextToMathML(text) {
  if (!text) return;
  const latex = convertAsciiMathToLatex(text);
  const mathML = convertLatexToMathMl(latex);
  const key1 = generateRandomId("ntex_");
  const key2 = generateRandomId("nmat_");

  return `<tex-math c-data="${latex}" key="${key1}"><math key="${key2}">${mathML}</math></tex-math>`;
}

const TexttoMath = (editor) => {
  let text = editor.selection?.getContent({ format: "text" });
  const node = editor.selection?.getNode();

  if (!text) return;
  let mathML = convertTextToMathML(text);
  const texKey = generateRandomId("tex_");
  const altKey = generateRandomId("alt_");
  if (node.tagName.toLowerCase() === "p" && node.getAttribute("data-latex")) {
    let latex = node.getAttribute("data-latex");
    let newmathML = convertLatexToMathMl(latex);
    mathML = convertMathML(newmathML, latex);
  }

  const allInlineIdSorted = editor.dom
    .select("inline-formula")
    .map((el) => el.getAttribute("id"))
    .filter((id) => id)
    .sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ""), 10);
      const numB = parseInt(b.replace(/\D/g, ""), 10);
      return numA - numB;
    });

  const lastId = allInlineIdSorted[allInlineIdSorted.length - 1] || "inl1";
  const newIdNum = parseInt(lastId.replace(/\D/g, ""), 10) + 1;
  const inlineId = `inl${newIdNum}`;

  const mathTag = `
    <inline-formula id="${inlineId}" key="${texKey}">
      <alternatives key="${altKey}">
        ${mathML}
      </alternatives>
    </inline-formula>
  `;
  editor.selection.setContent(mathTag);
  return "";
};

// -----------------------------MARK:Math to Text---------------------------------

const convertFormulaNodeToText = (node) => {
  const texmath = node.querySelector("tex-math");
  if (!texmath) return node.textContent;

  const latex = texmath.getAttribute("c-data") || "";
  const mathML = convertLatexToMathMl(latex);
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(mathML, "application/xml");
  const plainText = xmlDoc.documentElement.textContent;

  // Case 1: if tex-math is inside a <p>, replace tex-math with text
  if (texmath.closest("inline-formula")) {
    return plainText;
  }

  // Case 2: if tex-math is standalone, create a new <p>
  const tag = document.createElement("p");
  const key = generateRandomId("MTex_");
  tag.setAttribute("key", key);
  tag.setAttribute("data-latex", latex);
  tag.setAttribute("data-edit", "latex");
  tag.innerHTML = plainText;
  return tag;
};

const MathtoText = (editor, node) => {
  if (!node || !node.tagName) return;
  const text = convertFormulaNodeToText(node);
  const textNode = document.createTextNode(text);

  if (node.tagName === "INLINE-FORMULA") {
    node.parentNode.replaceChild(textNode, node);
  } else if (node.tagName === "DISP-FORMULA") {
    let parent = node.parentNode;
    const label = parent.querySelector("label");
    if (label) parent.removeChild(label);
    if (node.parentNode.tagName === "P") {
      node.parentNode.parentNode.replaceChild(text, node.parentNode);
    } else {
      node.parentNode.replaceChild(text, node);
    }
    ReorderEquations(editor);
  }
};

// -----------------------------------------MARK: Reorder Equations----------------------------------------

function getMappedNumber(map, oldId) {
  const newId = map.get(oldId);
  const match = newId?.match(/disp(\d+)/);
  return match ? match[1] : null;
}

function ReorderEquations(editor) {
  const dispFormulas = editor.dom.select("disp-formula");
  const idMap = new Map();
  let count = 1;

  dispFormulas.forEach((formula) => {
    const parentP = formula.closest("p");
    if (!parentP) return;
    const label = parentP.querySelector("label");
    if (!label || !/\d/.test(label.textContent)) return;

    const oldId = formula.getAttribute("id");
    if (!oldId) return;

    const newId = `disp${count}`;
    const newLabId = `${count}`;

    label.textContent = `(${count})`;
    label.setAttribute("id", newLabId);
    formula.setAttribute("id", newId);

    idMap.set(oldId, newId);
    count++;
  });
  const xrefs = editor.dom.select("xref[ref-type='disp-formula']");

  xrefs.forEach((xref) => {
    const oldId = xref.getAttribute("rid");
    const newId = idMap.get(oldId);
    if (!newId) return;

    xref.setAttribute("rid", newId);
    xref.setAttribute("href", `#${newId}`);
    xref.setAttribute("data-mce-href", `#${newId}`);

    const text = xref.textContent.trim();
    let prefix = "";
    let output = "";

    const rangeMatch = text.match(/^(Eq[s]?\.?)?\s*(\d+)\s*-\s*(\d+)$/i);

    const singleMatch = text.match(/^(Eq[s]?\.?)?\s*(\d+)$/i);

    if (rangeMatch) {
      prefix = rangeMatch[1] ? rangeMatch[1].trim() : "";
      const oldStartId = `disp${rangeMatch[2]}`;
      const oldEndId = `disp${rangeMatch[3]}`;
      const newStart = getMappedNumber(idMap, oldStartId);
      const newEnd = getMappedNumber(idMap, oldEndId);
      if (newStart && newEnd) {
        output = prefix
          ? `${prefix} ${newStart}${newEnd}`
          : `${newStart}${newEnd}`;
      }
    } else if (singleMatch) {
      prefix = singleMatch[1] ? singleMatch[1].trim() : "";
      const num = newId.match(/disp(\d+)/)?.[1];
      if (num) {
        output = prefix ? `${prefix} ${num}` : num;
      }
    }

    if (output) {
      xref.textContent = output;
      replaceXrefsHyphen(editor);
    }
  });
}

//  -----------------------------------------MARK:change all math format for initial Load  ---------------------------------
function replaceXrefsHyphen(editor) {
  if (!editor.dom) return;
  const xrefs = editor.dom.select("xref");
  addSpacesAroundFormulas(editor);
  for (let i = 0; i < xrefs.length - 1; i++) {
    const current = xrefs[i];

    let betweenNode = current.nextSibling;

    while (
      betweenNode &&
      betweenNode.nodeType === 3 &&
      betweenNode.textContent.trim() === ""
    ) {
      betweenNode = betweenNode.nextSibling;
    }

    if (
      betweenNode &&
      betweenNode.nodeType === 3 &&
      /^ *-+ *$/.test(betweenNode.textContent)
    ) {
      betweenNode.textContent = "\u2006\u2013\u2006";
    }
  }
}
function addSpacesAroundFormulas(editor) {
  if (!editor || !editor.dom) return;

  const spaceChar = "\u00A0";
  const hasTrailingSpace = (s) => /[\s\u00A0]$/.test(s);
  const hasLeadingSpace = (s) => /^[\s\u00A0]/.test(s);

  editor.dom.select("disp-formula, inline-formula").forEach((formula) => {
    const parent = formula.parentNode;
    if (!parent) return;

    let prev = formula.previousSibling;
    if (prev && prev.nodeType === Node.TEXT_NODE) {
      if (!hasTrailingSpace(prev.textContent)) {
        prev.textContent += spaceChar;
      }
    } else {
      parent.insertBefore(document.createTextNode(spaceChar), formula);
    }

    let next = formula.nextSibling;
    if (next && next.nodeType === Node.TEXT_NODE) {
      if (!hasLeadingSpace(next.textContent)) {
        next.textContent = spaceChar + next.textContent;
      }
    } else {
      parent.insertBefore(
        document.createTextNode(spaceChar),
        formula.nextSibling
      );
    }
  });

  editor.nodeChanged();
}

function convertMathML(mathML, latex) {
  const key1 = generateRandomId("ntex_");
  const key2 = generateRandomId("nmat_");
  // const final = cleanMathMLString(mathML);
  return `<tex-math c-data="${latex}" key="${key1}"><math key="${key2}">${mathML}</math></tex-math>`;
}

// function mathmlToText(mathmlString) {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(mathmlString, "application/xml");

//   function getTextRecursive(node) {
//     let text = "";
//     for (let child of node.childNodes) {
//       if (child.nodeType === 3) {
//         text += child.nodeValue;
//       } else if (child.nodeType === 1) {
//         text += getTextRecursive(child);
//       }
//     }
//     return text;
//   }

//   const mathNode = doc.documentElement;
//   return getTextRecursive(mathNode).replace(/\s+/g, " ").trim();
// }

// function cleanMathMLString(mathML) {
//   const parser = new DOMParser();
//   const xmlDoc = parser.parseFromString(mathML, "application/xml");

//   const moTags = xmlDoc.querySelectorAll("mo");
//   moTags.forEach((mo) => {
//     const text = mo.textContent.trim();
//     if (
//       text === "" ||
//       /^[\u2061\u2062\u2063\u2064\u200B-\u200D\uFEFF⁡⁢]*$/.test(text)
//     ) {
//       mo.parentNode.removeChild(mo);
//     }
//   });

//   return new XMLSerializer().serializeToString(xmlDoc);
// }

export {
  handleDisplaytoInline,
  handleInlinetoDisplay,
  ReorderEquations,
  replaceXrefsHyphen,
  TexttoMath,
  MathtoText,
  generateRandomId,
};
